import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Save } from "lucide-react";
import * as XLSX from "xlsx";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend as RechartsLegend, ResponsiveContainer } from "recharts";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { EmptyState } from "@/components/EmptyState";
import { fmtDate, fmtNum } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const DAY_MS = 24 * 60 * 60 * 1000;
const toISO = (d) => d.toISOString().slice(0, 10);
const parseISO = (s) => new Date(`${s}T00:00:00Z`);
const addDays = (isoDate, days) => toISO(new Date(parseISO(isoDate).getTime() + days * DAY_MS));
const daysBetween = (a, b) => Math.round((parseISO(b) - parseISO(a)) / DAY_MS);

function buildDateColumns(startISO, endISO) {
  const cols = [];
  const span = Math.max(daysBetween(startISO, endISO), 0);
  for (let i = 0; i <= span; i++) cols.push(addDays(startISO, i));
  return cols;
}

function buildWeekColumns(dateCols, projectStartISO) {
  const weeks = [];
  let current = null;
  dateCols.forEach((d) => {
    const weekIdx = Math.floor(daysBetween(projectStartISO, d) / 7) + 1;
    if (!current || current.weekIdx !== weekIdx) {
      current = { weekIdx, label: `W${weekIdx}`, dates: [] };
      weeks.push(current);
    }
    current.dates.push(d);
  });
  return weeks;
}

function varianceColor(actualPct, plannedPct) {
  if (plannedPct <= 0) return "text-muted-foreground";
  const gapPts = (plannedPct - actualPct) * 100;
  if (gapPts > 10) return "text-red-600 dark:text-red-400 font-semibold";
  if (gapPts > 0) return "text-amber-600 dark:text-amber-400 font-semibold";
  return "text-emerald-600 dark:text-emerald-400 font-semibold";
}

const PRODUCTIVITY_PILL = {
  ON_TRACK: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  AT_RISK: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  BEHIND: "bg-red-500/10 text-red-600 dark:text-red-400",
  NO_DATA: "bg-muted text-muted-foreground",
};

export default function PlanVsActual() {
  const { projectId, project, canWrite } = useProjectCtx();
  const canEdit = canWrite("CONSTRUCTION");
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [progressItems, setProgressItems] = useState([]);
  const [planByItem, setPlanByItem] = useState({});
  const [actualByItem, setActualByItem] = useState({});
  const [dateRange, setDateRange] = useState(null);
  const [view, setView] = useState("daily");
  const [groupFilter, setGroupFilter] = useState("all");
  const [blockFilter, setBlockFilter] = useState("all");
  const [behindOnly, setBehindOnly] = useState(false);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: wbsItems }, { data: catProgress }] = await Promise.all([
        api.get(`/projects/${projectId}/construction`),
        api.get(`/projects/${projectId}/category-progress`, { params: { category: "CONSTRUCTION" } }),
      ]);
      setItems(wbsItems);
      setProgressItems(catProgress.items || []);

      const leaves = wbsItems.filter((i) => !i.is_group);
      const scheduledDates = leaves.flatMap((i) => [i.construction?.scheduled_start_date, i.construction?.scheduled_end_date]).filter(Boolean);
      const todayISO = toISO(new Date());
      const earliestScheduled = scheduledDates.sort()[0];
      const latestScheduled = scheduledDates.sort().slice(-1)[0];
      const start = earliestScheduled || addDays(todayISO, -14);
      const end = (latestScheduled && latestScheduled > todayISO ? latestScheduled : todayISO);
      setDateRange({ start, end: daysBetween(start, end) > 400 ? addDays(start, 400) : end });

      if (leaves.length > 0) {
        const [{ data: plans }, { data: actuals }] = await Promise.all([
          api.get(`/projects/${projectId}/plan-entries`, { params: { date_from: start, date_to: end } }),
          api.get(`/projects/${projectId}/plan-entries/daily-actuals`, { params: { date_from: start, date_to: end } }),
        ]);
        const planMap = {};
        plans.forEach((p) => {
          planMap[p.wbs_item_id] = planMap[p.wbs_item_id] || {};
          planMap[p.wbs_item_id][p.plan_date] = Number(p.planned_qty);
        });
        setPlanByItem(planMap);
        setActualByItem(actuals || {});
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const leafItems = useMemo(() => items.filter((i) => !i.is_group), [items]);
  const groups = useMemo(() => items.filter((i) => i.is_group), [items]);
  const progressByItemId = useMemo(() => {
    const m = {};
    progressItems.forEach((p) => { m[p.item_id] = p; });
    return m;
  }, [progressItems]);

  const blockOptions = useMemo(
    () => Array.from(new Set(leafItems.map((i) => i.code.split(".")[0]))).sort(),
    [leafItems]
  );

  const visibleLeaves = useMemo(() => {
    return leafItems.filter((item) => {
      if (groupFilter !== "all" && item.parent_id !== groupFilter) return false;
      if (blockFilter !== "all" && item.code.split(".")[0] !== blockFilter) return false;
      if (behindOnly) {
        const p = progressByItemId[item.id];
        if (!p || p.actual_pct >= p.planned_pct) return false;
      }
      return true;
    });
  }, [leafItems, groupFilter, blockFilter, behindOnly, progressByItemId]);

  const dateCols = useMemo(() => (dateRange ? buildDateColumns(dateRange.start, dateRange.end) : []), [dateRange]);
  const weekCols = useMemo(
    () => (dateRange ? buildWeekColumns(dateCols, project?.start_date || dateRange.start) : []),
    [dateCols, dateRange, project]
  );
  const columns = view === "daily" ? dateCols.map((d) => ({ key: d, label: fmtDate(d).replace(/, \d{4}$/, ""), dates: [d] })) : weekCols.map((w) => ({ key: w.label, label: w.label, dates: w.dates }));

  const dirtyCount = Object.keys(dirty).length;

  const cellKey = (itemId, date) => `${itemId}|${date}`;

  const getPlanValue = (itemId, date) => {
    const k = cellKey(itemId, date);
    if (k in dirty) return dirty[k];
    return planByItem[itemId]?.[date] ?? "";
  };

  const setPlanValue = (itemId, date, value) => {
    const num = value === "" ? "" : Math.max(0, Number(value));
    setDirty((d) => ({ ...d, [cellKey(itemId, date)]: num }));
  };

  const columnPlanSum = (itemId, dates) =>
    dates.reduce((sum, d) => sum + (Number(getPlanValue(itemId, d)) || 0), 0);

  const columnActualSum = (itemId, dates) =>
    dates.reduce((sum, d) => sum + (actualByItem[itemId]?.find((a) => a.date === d)?.actual_qty || 0), 0);

  const saveChanges = async () => {
    setSaving(true);
    try {
      const entries = Object.entries(dirty).map(([key, planned_qty]) => {
        const [wbs_item_id, plan_date] = key.split("|");
        return { wbs_item_id, plan_date, planned_qty: planned_qty === "" ? 0 : planned_qty };
      });
      await api.put(`/projects/${projectId}/plan-entries/bulk`, { entries });
      toast.success(`Saved ${entries.length} plan cell(s)`);
      setDirty({});
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const exportExcel = () => {
    // Sheet name + "Actual" row values (cumulative-to-date, not per-column delta) are
    // aligned with excel_import.py's import_construction_plan_vs_actuals() expectations
    // so a file exported here re-imports cleanly via Workbook Import (see DATA_MAPPING.md #6).
    // Daily view's column key is the raw ISO date (YYYY-MM-DD, parseable by the importer's
    // as_date()); the weekly view's key is a "W1"/"W2" label that won't parse as a date, which
    // is intentional — only a daily-view export is meant to round-trip through the importer.
    const header = ["Code", "Activity", "Row", "UoM", "Scope", "Completed", "Balance", "% Plan", "% Actual", "Planned start", "Planned finish", ...columns.map((c) => c.key)];
    const rows = [header];
    visibleLeaves.forEach((item) => {
      const p = progressByItemId[item.id] || {};
      const scope = Number(item.quantity || 0);
      const completed = Number(p.completed_qty || 0);
      const totalDelta = dateCols.reduce((sum, d) => sum + (actualByItem[item.id]?.find((a) => a.date === d)?.actual_qty || 0), 0);
      let running = completed - totalDelta;
      rows.push([
        item.code, item.name, "Plan", item.uom || "", scope, completed, scope - completed,
        p.planned_pct ?? "", p.actual_pct ?? "", item.construction?.scheduled_start_date || "", item.construction?.scheduled_end_date || "",
        ...columns.map((c) => columnPlanSum(item.id, c.dates)),
      ]);
      rows.push([
        item.code, item.name, "Actual", item.uom || "", "", "", "", "", "", "", "",
        ...columns.map((c) => { running += columnActualSum(item.id, c.dates); return running; }),
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Construction PlanvsActuals");
    XLSX.writeFile(wb, `plan-vs-actual-${project?.code || "project"}.xlsx`);
  };

  if (loading) {
    return <div className="py-16 text-center text-sm text-muted-foreground" data-testid="plan-vs-actual-loading">Loading plan vs actual…</div>;
  }

  return (
    <div className="space-y-4" data-testid="plan-vs-actual-page">
      <PageHeader
        title="Plan vs Actual"
        description="Daily/weekly planned quantities against actuals logged from construction DPR entries."
        action={
          <div className="flex items-center gap-2">
            {dirtyCount > 0 && (
              <Button size="sm" onClick={saveChanges} disabled={saving} data-testid="plan-save-button">
                {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
                Save changes ({dirtyCount})
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={exportExcel} data-testid="plan-export-button">
              <Download className="mr-1.5 h-3.5 w-3.5" /> Export Excel
            </Button>
          </div>
        }
      />

      <Tabs defaultValue="grid" data-testid="plan-vs-actual-tabs">
        <TabsList>
          <TabsTrigger value="grid" data-testid="plan-tab-grid">Grid</TabsTrigger>
          <TabsTrigger value="block" data-testid="plan-tab-block">By block</TabsTrigger>
        </TabsList>

        <TabsContent value="grid" className="space-y-4">
          <FilterBar
            groups={groups}
            groupFilter={groupFilter}
            setGroupFilter={setGroupFilter}
            blockOptions={blockOptions}
            blockFilter={blockFilter}
            setBlockFilter={setBlockFilter}
            behindOnly={behindOnly}
            setBehindOnly={setBehindOnly}
            view={view}
            setView={setView}
          />

          {leafItems.length === 0 ? (
            <EmptyState title="No construction activities" description="Add construction WBS items to build a plan vs actual grid." testId="plan-vs-actual-empty-state" />
          ) : visibleLeaves.length === 0 ? (
            <EmptyState title="No activities match these filters" testId="plan-vs-actual-no-matches" />
          ) : (
            <PlanActualGrid
              groups={groups}
              visibleLeaves={visibleLeaves}
              progressByItemId={progressByItemId}
              columns={columns}
              canEdit={canEdit}
              view={view}
              getPlanValue={getPlanValue}
              setPlanValue={setPlanValue}
              columnPlanSum={columnPlanSum}
              columnActualSum={columnActualSum}
              dirty={dirty}
              cellKey={cellKey}
            />
          )}

          <Legend />
        </TabsContent>

        <TabsContent value="block">
          <BlockProgress projectId={projectId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FilterBar({ groups, groupFilter, setGroupFilter, blockOptions, blockFilter, setBlockFilter, behindOnly, setBehindOnly, view, setView }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3" data-testid="plan-filter-bar">
      <Select value={groupFilter} onValueChange={setGroupFilter}>
        <SelectTrigger className="w-44" data-testid="plan-filter-group"><SelectValue placeholder="Group" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All groups</SelectItem>
          {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={blockFilter} onValueChange={setBlockFilter}>
        <SelectTrigger className="w-36" data-testid="plan-filter-block"><SelectValue placeholder="Block" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All blocks</SelectItem>
          {blockOptions.map((b) => <SelectItem key={b} value={b}>Block {b}</SelectItem>)}
        </SelectContent>
      </Select>

      <label className="flex items-center gap-2 text-sm text-foreground">
        <Checkbox checked={behindOnly} onCheckedChange={setBehindOnly} data-testid="plan-filter-behind-only" />
        Behind plan only
      </label>

      <div className="ml-auto">
        <ToggleGroup type="single" value={view} onValueChange={(v) => v && setView(v)} data-testid="plan-view-toggle">
          <ToggleGroupItem value="daily" data-testid="plan-view-daily">Daily</ToggleGroupItem>
          <ToggleGroupItem value="weekly" data-testid="plan-view-weekly">Weekly</ToggleGroupItem>
        </ToggleGroup>
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-4 text-xs text-muted-foreground" data-testid="plan-legend">
      <span className="font-semibold text-foreground">Legend:</span>
      <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> On or ahead of plan</span>
      <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> 0–10 pts behind</span>
      <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-500" /> More than 10 pts behind</span>
    </div>
  );
}

function BlockProgress({ projectId }) {
  const [blocks, setBlocks] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/projects/${projectId}/block-progress`)
      .then(({ data }) => { if (active) setBlocks(data.blocks || []); })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [projectId]);

  const chartData = useMemo(
    () => (blocks || []).map((b) => ({ ...b, plannedPctLabel: b.planned_pct * 100, actualPctLabel: b.actual_pct * 100 })),
    [blocks]
  );

  if (loading) {
    return <div className="py-16 text-center text-sm text-muted-foreground" data-testid="block-progress-loading">Loading block progress…</div>;
  }
  if (!blocks || blocks.length === 0) {
    return <EmptyState title="No construction blocks yet" description="Set a block on your construction WBS items to see this view." testId="block-progress-empty-state" />;
  }

  return (
    <div className="space-y-4" data-testid="block-progress-view">
      <div className="rounded-xl border border-border bg-card p-4" data-testid="block-progress-chart-container">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
            <XAxis dataKey="block" stroke="#64748b" fontSize={11} />
            <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `${Math.round(v)}%`} />
            <Tooltip
              contentStyle={{ background: "#0F172A", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }}
              formatter={(v) => `${Number(v).toFixed(1)}%`}
            />
            <RechartsLegend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="plannedPctLabel" name="Planned %" fill="#E3A817" radius={[4, 4, 0, 0]} />
            <Bar dataKey="actualPctLabel" name="Actual %" fill="#10B981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border" data-testid="block-progress-table">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-card">
              {["Block", "Activities", "Completed", "Scope", "Planned", "Actual", "% Plan", "% Actual", "Variance"].map((h) => (
                <th key={h} className="whitespace-nowrap border-b border-border px-3 py-2 text-left text-xs font-semibold text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {blocks.map((b) => (
              <tr key={b.block} data-testid={`block-row-${b.block}`}>
                <td className="border-b border-border px-3 py-2 font-semibold text-foreground">{b.block}</td>
                <td className="border-b border-border px-3 py-2 font-mono text-xs">{b.item_count}</td>
                <td className="border-b border-border px-3 py-2 font-mono text-xs">{b.completed_items}</td>
                <td className="border-b border-border px-3 py-2 font-mono text-xs">{fmtNum(b.scope_qty)}</td>
                <td className="border-b border-border px-3 py-2 font-mono text-xs">{fmtNum(b.planned_qty)}</td>
                <td className="border-b border-border px-3 py-2 font-mono text-xs">{fmtNum(b.actual_qty)}</td>
                <td className="border-b border-border px-3 py-2 font-mono text-xs">{(b.planned_pct * 100).toFixed(1)}%</td>
                <td className="border-b border-border px-3 py-2 font-mono text-xs" data-testid={`block-actual-pct-${b.block}`}>{(b.actual_pct * 100).toFixed(1)}%</td>
                <td className={`border-b border-border px-3 py-2 font-mono text-xs ${varianceColor(b.actual_pct, b.planned_pct)}`}>{(b.variance_pct * 100).toFixed(1)} pts</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PlanActualGrid({ groups, visibleLeaves, progressByItemId, columns, canEdit, view, getPlanValue, setPlanValue, columnPlanSum, columnActualSum, dirty, cellKey }) {
  const byParent = useMemo(() => {
    const m = {};
    visibleLeaves.forEach((item) => {
      const key = item.parent_id || "__ungrouped__";
      (m[key] = m[key] || []).push(item);
    });
    return m;
  }, [visibleLeaves]);

  const groupOrder = [...groups.filter((g) => byParent[g.id]?.length), ...(byParent.__ungrouped__ ? [{ id: "__ungrouped__", name: "Ungrouped" }] : [])];

  return (
    <div className="overflow-x-auto rounded-xl border border-border" data-testid="plan-vs-actual-grid">
      <table className="min-w-full border-collapse text-sm">
        <thead className="sticky top-0 z-10 bg-card">
          <tr>
            {["Code", "Activity", "Row", "UoM", "Scope", "Completed", "Balance", "% Plan", "% Actual", "Productivity", "Start", "Finish"].map((h, idx) => (
              <th key={h} className={`sticky ${idx === 0 ? "left-0 z-20" : ""} whitespace-nowrap border-b border-border bg-card px-2 py-2 text-left text-xs font-semibold text-muted-foreground`}>{h}</th>
            ))}
            {columns.map((c) => (
              <th key={c.key} className="min-w-[64px] whitespace-nowrap border-b border-border bg-card px-2 py-2 text-center text-xs font-semibold text-muted-foreground">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groupOrder.map((group) => (
            <GroupBlock
              key={group.id}
              group={group}
              childItems={byParent[group.id] || []}
              progressByItemId={progressByItemId}
              columns={columns}
              canEdit={canEdit}
              view={view}
              getPlanValue={getPlanValue}
              setPlanValue={setPlanValue}
              columnPlanSum={columnPlanSum}
              columnActualSum={columnActualSum}
              dirty={dirty}
              cellKey={cellKey}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupBlock({ group, childItems, progressByItemId, columns, canEdit, view, getPlanValue, setPlanValue, columnPlanSum, columnActualSum, dirty, cellKey }) {
  const subtotal = childItems.reduce(
    (acc, item) => {
      const p = progressByItemId[item.id] || {};
      acc.planned += p.weighted_planned_pct || 0;
      acc.actual += p.weighted_actual_pct || 0;
      return acc;
    },
    { planned: 0, actual: 0 }
  );

  return (
    <>
      <tr className="bg-muted/40" data-testid={`plan-group-row-${group.id}`}>
        <td colSpan={9} className="sticky left-0 z-10 border-b border-border bg-muted/40 px-2 py-1.5 text-xs font-bold uppercase tracking-wide text-foreground">{group.name}</td>
        <td className="border-b border-border px-2 py-1.5 text-right text-xs font-mono text-muted-foreground" colSpan={3}>
          Subtotal — plan {(subtotal.planned * 100).toFixed(1)}% / actual {(subtotal.actual * 100).toFixed(1)}%
        </td>
        {columns.map((c) => <td key={c.key} className="border-b border-border" />)}
      </tr>
      {childItems.map((item) => (
        <ItemRows
          key={item.id}
          item={item}
          progress={progressByItemId[item.id]}
          columns={columns}
          canEdit={canEdit}
          view={view}
          getPlanValue={getPlanValue}
          setPlanValue={setPlanValue}
          columnPlanSum={columnPlanSum}
          columnActualSum={columnActualSum}
          dirty={dirty}
          cellKey={cellKey}
        />
      ))}
    </>
  );
}

function ItemRows({ item, progress, columns, canEdit, view, getPlanValue, setPlanValue, columnPlanSum, columnActualSum, dirty, cellKey }) {
  const p = progress || {};
  const prod = p.productivity;
  const scope = Number(item.quantity || 0);
  const completed = Number(p.completed_qty || 0);
  const balance = scope - completed;
  const pctColor = varianceColor(p.actual_pct || 0, p.planned_pct || 0);
  const leftCellClass = "sticky left-0 z-[5] border-b border-border bg-background px-2 py-1.5";

  return (
    <>
      <tr data-testid={`plan-row-${item.id}`}>
        <td className={`${leftCellClass} font-mono text-xs`} rowSpan={2}>{item.code}</td>
        <td className={`${leftCellClass} text-xs text-foreground`} rowSpan={2}>{item.name}</td>
        <td className={leftCellClass}><span className="rounded bg-gold-500/10 px-1.5 py-0.5 text-[10px] font-bold text-gold-600 dark:text-gold-400">P</span></td>
        <td className={`${leftCellClass} text-xs text-muted-foreground`} rowSpan={2}>{item.uom || "—"}</td>
        <td className={`${leftCellClass} font-mono text-xs`} rowSpan={2}>{fmtNum(scope)}</td>
        <td className={`${leftCellClass} font-mono text-xs`} rowSpan={2}>{fmtNum(completed)}</td>
        <td className={`${leftCellClass} font-mono text-xs`} rowSpan={2}>{fmtNum(balance)}</td>
        <td className={`${leftCellClass} font-mono text-xs`} rowSpan={2}>{((p.planned_pct || 0) * 100).toFixed(1)}%</td>
        <td className={`${leftCellClass} font-mono text-xs ${pctColor}`} rowSpan={2} data-testid={`plan-actual-pct-${item.id}`}>{((p.actual_pct || 0) * 100).toFixed(1)}%</td>
        <td className={`${leftCellClass} text-xs`} rowSpan={2} data-testid={`plan-productivity-${item.id}`}>
          {prod ? (
            <div className="flex flex-col gap-1">
              <span className={`inline-flex w-fit items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${PRODUCTIVITY_PILL[prod.status] || PRODUCTIVITY_PILL.NO_DATA}`}>
                {prod.status.replaceAll("_", " ")}
              </span>
              <span className="font-mono text-[11px] text-muted-foreground">
                {fmtNum(prod.current_productivity, 1)} / {fmtNum(prod.required_productivity, 1)} per day
              </span>
              {prod.manpower_linked && prod.required_manpower !== null && (
                <span className="font-mono text-[11px] text-muted-foreground">Manpower req: {fmtNum(prod.required_manpower, 1)}</span>
              )}
            </div>
          ) : "—"}
        </td>
        <td className={`${leftCellClass} font-mono text-xs text-muted-foreground`} rowSpan={2}>{fmtDate(item.construction?.scheduled_start_date)}</td>
        <td className={`${leftCellClass} font-mono text-xs text-muted-foreground`} rowSpan={2}>{fmtDate(item.construction?.scheduled_end_date)}</td>
        {columns.map((c) => {
          const isDaily = view === "daily";
          const dirtyHere = isDaily && dirty[cellKey(item.id, c.dates[0])] !== undefined;
          return (
            <td key={c.key} className={`border-b border-border px-1 py-1 text-center ${dirtyHere ? "bg-gold-500/10" : ""}`}>
              {isDaily && canEdit ? (
                <Input
                  type="number" min={0}
                  className="h-7 w-16 px-1 text-center text-xs"
                  value={getPlanValue(item.id, c.dates[0])}
                  onChange={(e) => setPlanValue(item.id, c.dates[0], e.target.value)}
                  data-testid={`plan-cell-${item.id}-${c.dates[0]}`}
                />
              ) : (
                <span className="font-mono text-xs" data-testid={`plan-cell-${item.id}-${c.key}`}>{fmtNum(columnPlanSum(item.id, c.dates))}</span>
              )}
            </td>
          );
        })}
      </tr>
      <tr className="bg-muted/20" data-testid={`actual-row-${item.id}`}>
        <td className="border-b border-border px-2 py-1.5"><span className="rounded bg-brandgreen-500/10 px-1.5 py-0.5 text-[10px] font-bold text-brandgreen-600 dark:text-brandgreen-400">A</span></td>
        {columns.map((c) => (
          <td key={c.key} className="border-b border-border px-1 py-1 text-center">
            <span className="font-mono text-xs text-muted-foreground" data-testid={`actual-cell-${item.id}-${c.key}`}>{fmtNum(columnActualSum(item.id, c.dates))}</span>
          </td>
        ))}
      </tr>
    </>
  );
}
